import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Webhook, Copy, RefreshCw, Trash2, Lock, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";
import { useAuth } from "@/context/AuthContext";
import { isBusinessTier } from "@/lib/tiers";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Iter 53 ,  Business-tier subscriber webhook ingest credentials panel.
 * Free/Pro see a grayed upgrade card.  Business generates/rotates/revokes
 * their key.  The raw key is shown exactly once on generate/rotate.
 */
export default function WebhookIngestPanel() {
  const { user } = useAuth();
  const isBusiness = isBusinessTier(user?.tier) ||
    ["admin", "super_admin"].includes(user?.role || "");
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [oneTimeKey, setOneTimeKey] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isBusiness) return;
    load();
  }, [isBusiness]);

  const load = () =>
    api.get("/integrations/webhook-ingest")
      .then((r) => setState(r.data))
      .catch(() => setState({ has_key: false }));

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/integrations/webhook-ingest");
      setOneTimeKey(r.data.key);
      toast.success("Webhook key generated. Copy it now ,  it will not be shown again.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate key");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke the current webhook key? Any partner using this key will start receiving 401 errors.")) return;
    setBusy(true);
    try {
      await api.delete("/integrations/webhook-ingest");
      setOneTimeKey(null);
      toast.success("Webhook key revoked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text, kind) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard write blocked");
    }
  };

  if (!isBusiness) {
    return (
      <div className="ae-card p-6 opacity-90" data-testid="webhook-ingest-panel-locked">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md border border-white/10 bg-slate-800/40 flex items-center justify-center">
            <Lock className="h-5 w-5 text-slate-500" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Business · Locked</div>
            <div className="font-heading text-lg text-slate-200">Webhook Ingestion</div>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              Business unlocks a per-account webhook endpoint so your VAs, data partners, and automations can push new opportunities straight into your pipeline. Includes signed authentication, rate limits, and automatic attribution.
            </p>
            <Link
              to="/app/billing?plan=business"
              data-testid="webhook-ingest-upgrade"
              className="inline-block mt-4 btn-primary text-xs px-3 py-1.5"
            >
              Upgrade to Business
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const endpoint = `${BACKEND}/api/ingest/webhook`;
  const curlExample = `curl -X POST "${endpoint}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "api_key": "${oneTimeKey || (state?.key_prefix ? `${state.key_prefix}.<REDACTED>` : "<your-key>")}",
    "source": "your-partner-system",
    "items": [{
      "title": "12-unit multifamily on Main",
      "asset_type": "multifamily",
      "address": "123 Main St",
      "city": "Tampa",
      "state": "FL",
      "zip_code": "33602",
      "owner_name": "Jane Doe",
      "owner_email": "jane@example.com",
      "owner_phone": "+18135550100",
      "seller_financing_flag": true
    }]
  }'`;

  return (
    <div className="ae-card p-6" data-testid="webhook-ingest-panel">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1">Business · Integrations</div>
          <div className="flex items-center gap-2">
            <Webhook className="h-5 w-5 text-emerald-400" strokeWidth={1.5} />
            <h2 className="font-heading text-xl tracking-tight">Webhook Ingestion</h2>
          </div>
          <p className="text-sm text-slate-400 mt-2">Push opportunities directly into your pipeline from any external system. Rate limit: {state?.rate_limit_per_hour ?? 100} requests/hour per key.</p>
        </div>
        {state?.has_key ? (
          <div className="flex flex-col gap-2 items-end">
            <button onClick={generate} disabled={busy} data-testid="webhook-rotate-btn" className="btn-ghost text-xs inline-flex items-center gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> Rotate
            </button>
            <button onClick={revoke} disabled={busy} data-testid="webhook-revoke-btn" className="text-xs text-rose-300 hover:text-rose-200 inline-flex items-center gap-1.5">
              <Trash2 className="h-3.5 w-3.5" /> Revoke
            </button>
          </div>
        ) : (
          <button onClick={generate} disabled={busy} data-testid="webhook-generate-btn" className="btn-primary text-xs">
            {busy ? "Generating…" : "Generate key"}
          </button>
        )}
      </div>

      {state?.has_key && (
        <div className="mt-5 grid sm:grid-cols-3 gap-3 text-xs">
          <Meta label="Key prefix" value={state.key_prefix} testid="webhook-key-prefix" />
          <Meta label="Created" value={state.created_at ? fmtDateTime(state.created_at) : ", "} />
          <Meta label="Last used" value={state.last_used_at ? fmtDateTime(state.last_used_at) : "Never"} testid="webhook-last-used" />
        </div>
      )}

      {oneTimeKey && (
        <div className="mt-5 p-4 border border-emerald-500/40 rounded-sm bg-emerald-500/5" data-testid="webhook-onetime-reveal">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">One-time reveal · copy now</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-xs text-emerald-200 break-all bg-black/30 px-3 py-2 rounded-sm">{oneTimeKey}</code>
            <button onClick={() => copy(oneTimeKey, "key")} className="btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="webhook-copy-key">
              {copied === "key" ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
              {copied === "key" ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">This key will not be shown again. If you lose it, use Rotate to generate a new one.</p>
        </div>
      )}

      {state?.has_key && (
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Endpoint</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-xs text-slate-300 bg-black/30 px-3 py-2 rounded-sm break-all">{endpoint}</code>
            <button onClick={() => copy(endpoint, "url")} className="btn-ghost text-xs inline-flex items-center gap-1.5">
              {copied === "url" ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
              {copied === "url" ? "Copied" : "Copy"}
            </button>
          </div>

          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-4 mb-2">Example request</div>
          <pre className="font-mono text-[11px] text-slate-300 bg-black/40 p-3 rounded-sm overflow-x-auto whitespace-pre-wrap" data-testid="webhook-curl-example">{curlExample}</pre>
          <p className="text-[11px] text-slate-500 mt-2">
            Every ingested opportunity is attributed to your account, scored automatically, and appears in your pipeline.
          </p>
        </div>
      )}
    </div>
  );
}

function Meta({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-slate-300 mt-1 font-mono break-all" data-testid={testid}>{value || ", "}</div>
    </div>
  );
}
